
import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, Button, CircularProgress,
         Alert, Chip, LinearProgress } from "@mui/material";
import { CloudUpload, CheckCircle, PictureAsPdf, LinkOff } from "@mui/icons-material";
import { COLORS, TYPE, RADIUS, CONTROL_HEIGHT } from "../../../styles/recruiterTheme";
import PublicPageShell, {
  PublicCard, PublicTitle, PublicStatus, readJson, networkMessage, validateFile, formatSize, MAX_UPLOAD_MB,
} from "../PublicPageShell";

const BASE = process.env.REACT_APP_API_BASE_URL;
const toBase64 = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result.split(",")[1]);
  r.onerror = () => rej(new Error("Could not read the selected file. Please choose it again."));
  r.readAsDataURL(file);
});

const overline = { fontSize: TYPE.overline, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: COLORS.muted, mb: 0.5 };

export default function CandidateUploadPage() {
  const { token } = useParams();
  const [meta,     setMeta]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [loadError,setLoadError]= useState("");
  const [reloadKey,setReloadKey]= useState(0);
  const [file,     setFile]     = useState(null);
  const [uploading,setUploading]= useState(false);
  const [done,     setDone]     = useState(false);
  const [error,    setError]    = useState("");
  const fileRef = useRef(null);
  const inFlight = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setLoadError("");
    fetch(`${BASE}/jd-review/public/upload/${token}`)
      .then(readJson)
      .then(d => {
        if (cancelled) return;
        if (!d.success) throw new Error(d.message || "This upload link is invalid or has expired.");
        setMeta(d);
      })
      .catch(e => { if (!cancelled) setLoadError(networkMessage(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, reloadKey]);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";            // allow re-picking the same file after an error
    if (!f) return;
    const msg = validateFile(f, [".pdf"]);
    if (msg) { setError(msg); return; }
    setError(""); setFile(f);
  };

  const handleUpload = async () => {
    if (!file || inFlight.current) return;
    inFlight.current = true;
    setUploading(true); setError("");
    try {
      const b64 = await toBase64(file);
      const res = await fetch(`${BASE}/jd-review/public/upload/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ file_b64: b64 }),
      }).then(readJson);
      if (!res.success) throw new Error(res.message || "Upload failed. Please try again.");
      setDone(true);
    } catch(e) { setError(networkMessage(e)); }
    finally { setUploading(false); inFlight.current = false; }
  };

  const skills = String(meta?.skills_required || "").split(",").map(s => s.trim()).filter(Boolean);

  let body;
  if (loading) {
    body = <PublicStatus loading message="Loading your upload link…" />;
  } else if (loadError) {
    const network = /reach the server|HTTP 5/.test(loadError);
    body = (
      <PublicStatus tone="error" icon={<LinkOff />} title={network ? "Could not load this page" : "Link unavailable"}
        message={network ? loadError : `${loadError} Please contact your recruiter for a new link.`}
        action={network ? { label: "Try again", onClick: () => setReloadKey(k => k + 1) } : null} />
    );
  } else if (done) {
    body = (
      <PublicStatus tone="success" icon={<CheckCircle />} title="Resume submitted"
        message="Our senior reviewer has been notified and will review your resume shortly. You can close this page." />
    );
  } else {
    body = (
      <>
        <PublicTitle title={`Hi ${meta?.candidate_name || "there"}`}
          subtitle="You've been shortlisted for the role below. Please tailor your resume to match the job description and upload it here." />

        {meta?.rejection_count > 0 && meta?.feedback && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Reviewer feedback on your previous upload:</strong> {meta.feedback}
          </Alert>
        )}

        <PublicCard>
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" mb={meta?.job_description || skills.length ? 2 : 0}>
            <Typography sx={{ fontWeight: 700, fontSize: TYPE.cardTitle, color: COLORS.ink, wordBreak: "break-word" }}>
              {meta?.job_title || "Role"}
            </Typography>
            {meta?.client_name && (
              <Chip label={meta.client_name} size="small" sx={{ bgcolor: COLORS.accentSoft, color: COLORS.primaryDark }} />
            )}
          </Box>
          {meta?.job_description && (
            <>
              <Typography sx={overline}>Job description</Typography>
              <Typography sx={{ fontSize: TYPE.small, whiteSpace: "pre-wrap", wordBreak: "break-word", color: COLORS.ink }}>
                {meta.job_description}
              </Typography>
            </>
          )}
          {skills.length > 0 && (
            <Box mt={2}>
              <Typography sx={overline}>Required skills</Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {skills.map((s, i) => (
                  <Chip key={i} label={s} size="small" sx={{ bgcolor: COLORS.surfaceTint, color: COLORS.primaryDark, border: `1px solid ${COLORS.border}` }} />
                ))}
              </Box>
            </Box>
          )}
        </PublicCard>

        <PublicCard>
          <Typography sx={{ fontWeight: 700, fontSize: TYPE.cardTitle, color: COLORS.ink, mb: 1.5 }}>Your tailored resume</Typography>
          <Box role="button" tabIndex={0}
            onClick={() => !uploading && fileRef.current?.click()}
            onKeyDown={e => { if ((e.key === "Enter" || e.key === " ") && !uploading) { e.preventDefault(); fileRef.current?.click(); } }}
            sx={{
              border: `2px ${file ? "solid" : "dashed"} ${file ? COLORS.success : COLORS.borderStrong}`,
              borderRadius: `${RADIUS.md}px`, p: 2, cursor: uploading ? "default" : "pointer",
              bgcolor: file ? COLORS.successSoft : COLORS.surfaceTint,
              display: "flex", alignItems: "center", gap: 2,
              "&:hover": uploading ? {} : { bgcolor: file ? COLORS.successSoft : COLORS.accentSoft },
            }}>
            <PictureAsPdf sx={{ fontSize: 36, color: file ? COLORS.success : COLORS.primaryLight, flexShrink: 0 }} />
            <Box flex={1} minWidth={0}>
              {file
                ? <><Typography noWrap title={file.name} sx={{ fontWeight: 700, fontSize: TYPE.body, color: COLORS.success }}>{file.name}</Typography>
                    <Typography sx={{ fontSize: TYPE.caption, color: COLORS.muted }}>{formatSize(file.size)} · click to choose a different file</Typography></>
                : <><Typography sx={{ fontWeight: 600, fontSize: TYPE.body, color: COLORS.primary }}>Click to select your tailored resume</Typography>
                    <Typography sx={{ fontSize: TYPE.caption, color: COLORS.muted }}>PDF only · max {MAX_UPLOAD_MB} MB</Typography></>
              }
            </Box>
            <input ref={fileRef} type="file" accept=".pdf,application/pdf" hidden onChange={pickFile} />
          </Box>

          {uploading && <LinearProgress sx={{ mt: 1.5, borderRadius: 1 }} />}
          {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}

          <Button fullWidth variant="contained" onClick={handleUpload}
            disabled={!file || uploading}
            sx={{ mt: 2, height: CONTROL_HEIGHT.lg, fontWeight: 600, textTransform: "none" }}
            startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <CloudUpload />}>
            {uploading ? "Uploading…" : "Submit resume for review"}
          </Button>
        </PublicCard>
      </>
    );
  }

  return <PublicPageShell subtitle="Tailored resume upload">{body}</PublicPageShell>;
}
